// Jedno zrodlo prawdy dla kombinacji stolikow ("Kombinacje Stolikow" od klienta).
// Uzywane zarowno przez frontend (auto-przydzial stolikow w formularzu rezerwacji)
// jak i przez backend (import zajetosci z GoPOS).
//
// WAZNE: liczby to `id` stolika, ktore w naszej bazie sa rowne widocznemu numerowi
// stolika (nazwa stolika z GoPOS wpada wprost jako table_id).
//
// Zasady:
//  - Kazdy wpis w tablicy to JEDNA dozwolona kombinacja; kolejnosc = priorytet.
//  - Dla 1-2 osob niektore stoliki blokuja sasiada (nie wolno ich rozdzielac):
//    3<->4, 8<->9, 10<->11. Dlatego "pojedynczy" stolik dla pary to w praktyce
//    para id (np. [3, 4]) — rezerwacja zajmuje oba stoliki.

export type CapacityBucket = '1-2' | '3-4' | '5-6' | '7-8' | '9-10' | '11-12';

export function getCapacityBucket(guests: number): CapacityBucket | null {
  if (guests <= 0) {
    return null;
  }
  if (guests <= 2) {
    return '1-2';
  }
  if (guests <= 4) {
    return '3-4';
  }
  if (guests <= 6) {
    return '5-6';
  }
  if (guests <= 8) {
    return '7-8';
  }
  if (guests <= 10) {
    return '9-10';
  }
  if (guests <= 12) {
    return '11-12';
  }
  return null;
}

// Sala glowna (stoliki 1-18).
export const RESTAURANT_COMBOS: Record<CapacityBucket, number[][]> = {
  // Najpierw dedykowane dwuosobowe stoliki 5 i 16, potem pozostale pojedyncze
  // (6, 7), a na koncu "podwojne" pary blokujace sasiada (3<->4, 8<->9, 10<->11).
  // Dzieki temu rezerwacja dla 2 osob nie zajmuje niepotrzebnie pary stolikow,
  // gdy wolny jest zwykly dwojak.
  '1-2': [[5], [16], [6], [7], [3, 4], [8, 9], [10, 11]],
  '3-4': [[1, 2], [3, 4], [6, 7], [8, 9], [12, 13], [14, 15], [17, 18]],
  '5-6': [
    [3, 4, 5],
    [4, 5, 6],
    [5, 6, 7],
    [6, 7, 8],
    [7, 8, 9],
    [16, 15, 14],
  ],
  '7-8': [
    [3, 4, 5, 6],
    [4, 5, 6, 7],
    [5, 6, 7, 8],
    [6, 7, 8, 9],
    [12, 13, 14, 15],
  ],
  // Kombinacje 9-12 os. nie sa dostepne w formularzu online (max 8 os.),
  // ale sa potrzebne do wnioskowania zajetosci z GoPOS (goscie "z ulicy").
  '9-10': [
    [3, 4, 5, 6, 7],
    [4, 5, 6, 7, 8],
    [5, 6, 7, 8, 9],
    [12, 13, 14, 15, 16],
  ],
  '11-12': [
    [3, 4, 5, 6, 7, 8],
    [4, 5, 6, 7, 8, 9],
  ],
};

// Ogrodek (stoliki 20-33). Brak kombinacji dla 7-8 osob (max 6 osob w ogrodku).
export const GARDEN_COMBOS: Record<CapacityBucket, number[][]> = {
  '1-2': [
    [20],
    [21],
    [22],
    [23],
    [24],
    [25],
    [26],
    [27],
    [28],
    [29],
    [33],
  ],
  '3-4': [
    [20, 21],
    [22, 23],
    [25, 26],
    [27, 28],
    [30, 31],
    [31, 32],
  ],
  '5-6': [
    [30, 31, 32],
    [23, 22, 21],
    [28, 27, 26],
    [26, 25, 24],
    [20, 21, 22],
  ],
  // Ogrodek: brak kombinacji dla 7+ osob (max 6 osob).
  '7-8': [],
  '9-10': [],
  '11-12': [],
};

/**
 * Wnioskowanie zajetosci na podstawie danych z GoPOS.
 *
 * GoPOS mowi "przy stoliku X (numer) siedzi N osob". Zgodnie z lista klienta
 * oznacza to, ze zajeta jest cala kombinacja dla tej liczby osob, ktorej stolik X
 * jest "kotwica" (pierwszym stolikiem). Np. stolik 3 + 4 osoby => zajete 3 i 4;
 * stolik 3 + 6 osob => zajete 3, 4, 5.
 *
 * UWAGA: operuje na NUMERACH stolikow (table_number), nie na id. Sale/Ogrodek
 * rozroznia flaga `isGarden` (numery 20-27 wystepuja w obu salach), dlatego trzeba
 * ja podac jawnie — nie da sie jej wywnioskowac z samego numeru.
 *
 * Zwraca pelny zestaw zajetych NUMEROW stolikow. Gdy nie da sie jednoznacznie
 * dopasowac kombinacji (liczba osob spoza listy albo stolik nie jest kotwica
 * zadnej kombinacji) — zwraca konserwatywnie sam stolik X.
 */
export function expandOccupiedTables(
  anchor: number,
  guests: number,
  isGarden: boolean
): number[] {
  const bucket = getCapacityBucket(guests);
  if (!bucket) {
    return [anchor];
  }
  const map = isGarden ? GARDEN_COMBOS : RESTAURANT_COMBOS;
  const combos = map[bucket] || [];
  // 1) Stolik X jest kotwica (pierwszym) kombinacji — regula z listy klienta.
  const anchored = combos.find((combo) => combo[0] === anchor);
  if (anchored) {
    return [...anchored];
  }
  // 2) Stolik X wystepuje w dokladnie jednej kombinacji — uzyj jej.
  const containing = combos.filter((combo) => combo.includes(anchor));
  if (containing.length === 1) {
    return [...containing[0]];
  }
  // 3) Niejednoznaczne — blokuj tylko zgloszony stolik.
  return [anchor];
}
